// ─────────────────────────────────────────────
//  detectDod — find Tampa's DOD (their AIC) from the sheet's FORMATTING.
//
//  The sheet marks the Tampa Doctor-of-the-Day by making their attending code
//  BOLD or a DIFFERENT COLOUR in the attending column (user-confirmed
//  2026-07-07) — plain values carry no such tag, so the import fetches the
//  column's formatting (sheetsClient.readAttendingFormats) and this pure
//  function picks the outlier:
//
//    1. Build the MAJORITY format (bold+colour combo) across the facility's
//       attending cells — that's the "normal" styling.
//    2. Codes whose cells deviate from the majority are candidates; the code
//       with the most deviating cells wins.
//    3. No deviation at all → null (no DOD marked today).
//
//  rows   — parsed rows for ONE facility ({ attendingShortCode, sheetRow }).
//  formats — array from readAttendingFormats, indexed by sheetRow-1.
// ─────────────────────────────────────────────

const fmtKey = (f) => `${f?.bold ? "B" : "-"}|${f?.colorKey || ""}`;

export function detectDodCode(rows = [], formats = null) {
  if (!Array.isArray(formats) || !formats.length) return null;
  const cells = [];
  for (const r of rows || []) {
    if (!r?.attendingShortCode || !r.sheetRow) continue;
    const f = formats[r.sheetRow - 1];
    if (!f) continue;
    cells.push({ code: r.attendingShortCode, key: fmtKey(f) });
  }
  if (cells.length < 2) return null;

  // Majority format = the most common key.
  const keyCounts = new Map();
  for (const c of cells) keyCounts.set(c.key, (keyCounts.get(c.key) || 0) + 1);
  const majorityKey = [...keyCounts.entries()].sort((a, b) => b[1] - a[1])[0][0];

  // Deviating cells, tallied per attending code.
  const votes = new Map();
  for (const c of cells) {
    if (c.key === majorityKey) continue;
    votes.set(c.code, (votes.get(c.code) || 0) + 1);
  }
  if (!votes.size) return null;
  // Guard: the marked DOD must be a clear MINORITY. If half or more of the
  // cells deviate from the "majority" style, the formatting is too noisy to
  // trust — don't guess.
  const deviating = [...votes.values()].reduce((a, b) => a + b, 0);
  if (deviating >= cells.length / 2) return null;
  return [...votes.entries()].sort((a, b) => b[1] - a[1])[0][0];
}
